/**
 * HTTP transport for the Mesterplan API client.
 *
 * Wraps `fetch` with three behaviours every cluster consumer relies on:
 *  1. Attaches the current access token when a `getToken` callback is supplied.
 *  2. Serialises JSON bodies + parses JSON responses.
 *  3. Returns a discriminated `ApiResult<T>` so callers don't throw on expected
 *     HTTP errors (404 / 409 / 422) — they branch on `result.ok`.
 *
 * Unexpected errors (network failures, 5xx, malformed JSON) still throw — the
 * consuming hook layer maps those to user-visible errors.
 */

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: ApiError };

export interface ApiError {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  /** Machine-readable ProblemDetails extension, e.g. `org_not_available`. */
  code?: string;
  errors?: Record<string, string[]>;
  body?: unknown;
}

/**
 * Why a company is locked (M9). `awaiting_billing` is the window between Trackrs
 * enabling Mesterplan and the first billing state arriving; `module_cancelled`
 * means the Trackrs owner switched the Mesterplan module off.
 */
export type CompanyLockReason = "awaiting_billing" | "module_cancelled";

/**
 * The body of a **423** from `CompanyLockMiddleware`: every org-scoped call on a
 * locked company answers with this instead of running the endpoint. The web and
 * the app key their lock screen on `error === "company_locked"`.
 */
export interface CompanyLockedBody {
  error: "company_locked";
  reason: CompanyLockReason | null;
  /** Where the Trackrs owner switches Mesterplan back on. */
  reenableUrl: string;
}

export const COMPANY_LOCKED_STATUS = 423;

/** Whether a parsed error body is the 423 company-lock body. */
export function isCompanyLockedBody(v: unknown): v is CompanyLockedBody {
  if (typeof v !== "object" || v === null) return false;
  const obj = v as Record<string, unknown>;
  return obj.error === "company_locked" && typeof obj.reenableUrl === "string";
}

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type RequestCredentialsLike = "omit" | "same-origin" | "include";
/**
 * Structural stand-in for the DOM `AbortSignal`. Exported because the endpoint
 * signatures use it: this package is typechecked by React Native consumers
 * (`clients/rn-hooks`) whose lib config has no DOM, so naming the global
 * `AbortSignal` in a public signature breaks their build.
 */
export type AbortSignalLike = { readonly aborted: boolean };
type FetchRequestInit = {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignalLike;
  credentials?: RequestCredentialsLike;
};
type FetchResponseLike = {
  ok: boolean;
  status: number;
  text(): Promise<string>;
  /** Optional so existing fetch stubs (which only implement text()) keep typechecking. */
  arrayBuffer?(): Promise<ArrayBuffer>;
  /** Optional for the same reason; only read by `getBinaryDocument`. */
  headers?: { get(name: string): string | null };
};

/**
 * A binary response whose media type the caller needs to branch on — e.g. the creditor
 * invoice preview, which serves either the supplier's PDF or Sproom's HTML depending on
 * what the received document carried.
 */
export interface BinaryDocument {
  content: ArrayBuffer;
  /** The response's Content-Type; `application/octet-stream` when the transport exposes no headers. */
  contentType: string;
}
type FetchLike = (url: string, init?: any) => Promise<FetchResponseLike>;

export interface HttpClientOptions {
  /** Base URL of the .NET API. Trailing slashes are tolerated. */
  baseUrl: string;
  /**
   * Supplies the current access token. Called on every request so the client
   * always uses the freshest token. Omit it when the transport uses cookies.
   */
  getToken?: () => Promise<string | null> | string | null;
  /** Optional headers resolved for every request (e.g. active-org selection). */
  getHeaders?: () => Promise<Record<string, string> | null | undefined> | Record<string, string> | null | undefined;
  /** Optional fetch credentials mode. Use `"include"` for cookie-based BFF calls. */
  credentials?: RequestCredentialsLike;
  /**
   * Optional `fetch` implementation. Defaults to `globalThis.fetch`. Lets tests
   * inject a stub.
   */
  fetchImpl?: FetchLike;
  /**
   * Called whenever a response is a 423 `company_locked` (see
   * {@link CompanyLockedBody}), before the result is returned to the caller. Lets
   * the app root swap in its lock screen from one place instead of every call
   * site checking for 423. The call still resolves to `{ ok: false, status: 423 }`.
   */
  onCompanyLocked?: (lock: CompanyLockedBody) => void;
  /**
   * Called on a 403 whose ProblemDetails `code` is `org_not_available`: the active-org
   * middleware's answer when the user no longer has access to the org (a supplier whose
   * grant was removed). A plain permission 403 never raises it. The call still resolves
   * to `{ ok: false, status: 403 }`. It receives the org the REQUEST was made for (the
   * `X-Active-Org` it sent), so a late answer for an org the user has since left is
   * recognisable; `activeOrg` is null when the request carried none.
   */
  onOrgNotAvailable?: (request: { activeOrg: string | null }) => void;
}

/** The `X-Active-Org` a request carried (header names are case-insensitive), or null. */
function activeOrgHeader(headers: Record<string, string>): string | null {
  const key = Object.keys(headers).find((k) => k.toLowerCase() === "x-active-org");
  return key ? headers[key] ?? null : null;
}

export const ORG_NOT_AVAILABLE_CODE = "org_not_available";

/**
 * True for the 403 `org_not_available` answer, and only for it. Reads `code` off the parsed
 * error and, for a body that did not parse as ProblemDetails (no numeric `status`), off `body`.
 */
export function isOrgNotAvailableError(
  status: number,
  error: { code?: unknown; body?: unknown } | null | undefined,
): boolean {
  if (status !== 403 || !error) return false;
  if (error.code === ORG_NOT_AVAILABLE_CODE) return true;
  const body = error.body;
  return typeof body === "object" && body !== null && (body as { code?: unknown }).code === ORG_NOT_AVAILABLE_CODE;
}

export interface RequestOptions {
  /**
   * Query parameters appended as `?k=v&k2=v2`. Values are URI-encoded.
   *
   * An array value is emitted as a repeated key (`?k=a&k=b`), which is what
   * ASP.NET Core model binding expects for a collection parameter. A
   * comma-joined single value does not bind.
   */
  query?: Record<
    string,
    string | number | boolean | null | undefined | readonly (string | number | boolean)[]
  >;
  /** JSON body. Sent as `application/json` unless `body` is `FormData`. */
  body?: unknown;
  signal?: AbortSignalLike;
  /** Additional headers merged on top of the default Authorization + Accept. */
  headers?: Record<string, string>;
}

/**
 * Builds the `{ signal }` fragment of a RequestOptions object.
 *
 * Exists so the `exactOptionalPropertyTypes` workaround lives in ONE place: under
 * that flag an explicit `signal: undefined` is not the same as an absent property,
 * so every call site would otherwise repeat the conditional spread and they would
 * drift apart.
 */
export function withSignal(options: { signal?: AbortSignalLike } = {}): { signal?: AbortSignalLike } {
  return options.signal ? { signal: options.signal } : {};
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly getToken: HttpClientOptions["getToken"];
  private readonly getHeaders: HttpClientOptions["getHeaders"];
  private readonly credentials: RequestCredentialsLike | undefined;
  private readonly fetchImpl: FetchLike;
  private readonly onCompanyLocked: HttpClientOptions["onCompanyLocked"];
  private readonly onOrgNotAvailable: HttpClientOptions["onOrgNotAvailable"];

  constructor(opts: HttpClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.getToken = opts.getToken;
    this.getHeaders = opts.getHeaders;
    this.credentials = opts.credentials;
    this.onCompanyLocked = opts.onCompanyLocked;
    this.onOrgNotAvailable = opts.onOrgNotAvailable;
    const globalFetch = (globalThis as { fetch?: FetchLike }).fetch;
    if (opts.fetchImpl) {
      this.fetchImpl = opts.fetchImpl;
    } else if (globalFetch) {
      // Bind to the global object: the browser/Node `fetch` throws "Illegal
      // invocation" if called with `this` set to anything other than the global
      // (which happens when stored on and called via an instance field).
      this.fetchImpl = globalFetch.bind(globalThis);
    } else {
      throw new Error("No fetch implementation available");
    }
  }

  async request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
    const url = this.buildUrl(path, options.query);
    const token = this.getToken ? await this.getToken() : null;
    const resolvedHeaders = this.getHeaders ? await this.getHeaders() : null;
    const headers: Record<string, string> = {
      accept: "application/json",
      ...(resolvedHeaders ?? {}),
      ...(options.headers ?? {}),
    };
    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    let body: unknown;
    if (options.body !== undefined && options.body !== null) {
      if (isFormData(options.body)) {
        body = options.body;
        // Don't set content-type — fetch sets the multipart boundary itself.
      } else {
        body = JSON.stringify(options.body);
        headers["content-type"] = "application/json";
      }
    }

    const init: FetchRequestInit = { method, headers };
    if (body !== undefined) init.body = body;
    if (options.signal) init.signal = options.signal;
    if (this.credentials) init.credentials = this.credentials;
    const res = await this.fetchImpl(url, init);

    if (res.status === 204) {
      return { ok: true, status: 204, data: undefined as T };
    }

    const parsed = parseBody(await res.text());

    if (res.ok) {
      return { ok: true, status: res.status, data: parsed as T };
    }
    return this.toFailure(res.status, parsed, headers);
  }

  /**
   * Maps a non-ok response to the `ok: false` result, and raises the company-lock
   * event for a 423 lock body. Shared by the JSON and binary paths so a locked
   * company is noticed on a PDF download exactly as on a list call.
   */
  private toFailure(
    status: number,
    parsed: unknown,
    requestHeaders: Record<string, string> = {},
  ): { ok: false; status: number; error: ApiError } {
    if (status === COMPANY_LOCKED_STATUS && isCompanyLockedBody(parsed)) {
      this.onCompanyLocked?.(parsed);
    }
    const error = toApiError(status, parsed);
    if (isOrgNotAvailableError(status, error)) {
      this.onOrgNotAvailable?.({ activeOrg: activeOrgHeader(requestHeaders) });
    }
    return { ok: false, status, error };
  }

  get<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request("GET", path, options);
  }

  /**
   * GETs a binary response (e.g. a PDF) as an `ArrayBuffer`, going through the
   * same auth / base-url / credentials pipeline as `request<T>`. Web callers
   * wrap the buffer in a `Blob`; we keep DOM types out of the shared lib.
   *
   * On a non-ok response the body is read as text and mapped to `ApiError`
   * exactly like `request<T>` — so a JSON ProblemDetails error is parsed
   * correctly rather than handed back as raw binary.
   */
  async getBinary(path: string, options: RequestOptions = {}): Promise<ApiResult<ArrayBuffer>> {
    const result = await this.getBinaryDocument(path, options);
    return result.ok ? { ok: true, status: result.status, data: result.data.content } : result;
  }

  /**
   * Like `getBinary`, but also reports the response's Content-Type, for endpoints that
   * legitimately answer with different media types (PDF vs HTML). Falls back to
   * `application/octet-stream` when the injected fetch stub exposes no headers.
   */
  async getBinaryDocument(path: string, options: RequestOptions = {}): Promise<ApiResult<BinaryDocument>> {
    const url = this.buildUrl(path, options.query);
    const token = this.getToken ? await this.getToken() : null;
    const resolvedHeaders = this.getHeaders ? await this.getHeaders() : null;
    const headers: Record<string, string> = {
      accept: "application/pdf",
      ...(resolvedHeaders ?? {}),
      ...(options.headers ?? {}),
    };
    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    const init: FetchRequestInit = { method: "GET", headers };
    if (options.signal) init.signal = options.signal;
    if (this.credentials) init.credentials = this.credentials;
    const res = await this.fetchImpl(url, init);

    if (res.ok) {
      if (typeof res.arrayBuffer !== "function") {
        throw new Error("Fetch response does not support arrayBuffer()");
      }
      const buffer = await res.arrayBuffer();
      const contentType = res.headers?.get("content-type") ?? "application/octet-stream";
      return { ok: true, status: res.status, data: { content: buffer, contentType } };
    }

    return this.toFailure(res.status, parseBody(await res.text()), headers);
  }
  post<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request("POST", path, options);
  }
  put<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request("PUT", path, options);
  }
  patch<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request("PATCH", path, options);
  }
  delete<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request("DELETE", path, options);
  }

  private buildUrl(path: string, query?: RequestOptions["query"]): string {
    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    if (!query) return url;
    const parts: string[] = [];
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        // Repeated key, not a comma-joined value. An empty array deliberately
        // emits nothing — the caller means "no values", and any filter that
        // needs to distinguish that from "unset" must say so another way.
        for (const item of v) {
          if (item === undefined || item === null) continue;
          parts.push(`${encodeQueryComponent(k)}=${encodeQueryComponent(String(item))}`);
        }
        continue;
      }
      parts.push(`${encodeQueryComponent(k)}=${encodeQueryComponent(String(v))}`);
    }
    const qs = parts.join("&");
    return qs ? `${url}?${qs}` : url;
  }
}

/** JSON when the body parses, the raw text otherwise (plain-text 5xx pages), null when empty. */
function parseBody(text: string): unknown {
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function toApiError(status: number, parsed: unknown): ApiError {
  if (isProblemDetails(parsed)) {
    return { ...parsed, status, body: parsed };
  }
  if (typeof parsed === "string") {
    return { status, detail: parsed };
  }
  if (isInlineErrorBody(parsed)) {
    return {
      status,
      body: parsed,
      ...(parsed.detail ?? parsed.error ? { detail: parsed.detail ?? parsed.error } : {}),
    };
  }
  return { status, body: parsed };
}

function isProblemDetails(v: unknown): v is ApiError {
  if (typeof v !== "object" || v === null) return false;
  const obj = v as Record<string, unknown>;
  // RFC 7807 mandates `status` for problem-details payloads. Requiring it
  // prevents false positives — e.g. a plain `{ title: "..." }` business
  // response that happens to share a key name with the problem-details schema.
  return typeof obj.status === "number";
}

function isInlineErrorBody(
  v: unknown,
): v is { error?: string; detail?: string } {
  if (typeof v !== "object" || v === null) return false;
  const obj = v as Record<string, unknown>;
  return typeof obj.error === "string" || typeof obj.detail === "string";
}

function isFormData(value: unknown): boolean {
  const formDataCtor = (globalThis as { FormData?: new (...args: never[]) => object }).FormData;
  return typeof formDataCtor === "function" && value instanceof formDataCtor;
}

function encodeQueryComponent(value: string): string {
  return encodeURIComponent(value).replace(/%20/g, "+");
}
